import type React from "react";
import { useTranslation } from "../hooks/useTranslation";
import appStyles from "../styles/styles";
import type { RecordingFile } from "../types/models";
import { getSessionId, isWithinLastHour } from "../utils/sessionUtils";

interface RecordingItemProps {
	recording: RecordingFile;
	expandedId: string | null;
	setExpandedId: (id: string | null) => void;
	selectedRecordings: Set<string>;
	toggleSelection: (id: string, e: React.MouseEvent) => void;
	downloadFile: (recording: RecordingFile) => void;
	downloadTranscriptionText: (text: string, timestamp: string) => void;
	deleteRecording: (id: string) => void;
	formatDate: (dateStr: string) => string;
	formatDuration: (seconds: number) => string;
	formatFileSize: (file: File) => string;
}

export const RecordingItem: React.FC<RecordingItemProps> = ({
	recording,
	expandedId,
	setExpandedId,
	selectedRecordings,
	toggleSelection,
	downloadFile,
	downloadTranscriptionText,
	deleteRecording,
	formatDate,
	formatDuration,
	formatFileSize,
}) => {
	const t = useTranslation();

	return (
		<div
			key={recording.id}
			className={`${appStyles.recordingHistoryItem(expandedId === recording.id)} ${expandedId === recording.id ? "mb-5" : "mb-3"}`}
		>
			<div
				className={`${appStyles.recordingHistoryItemHeader} p-3 md:p-4 flex justify-between w-full text-left cursor-pointer`}
				onClick={() => setExpandedId(expandedId === recording.id ? null : recording.id)}
				onKeyDown={(e) => {
					if (e.key === "Enter" || e.key === " ") {
						setExpandedId(expandedId === recording.id ? null : recording.id);
					}
				}}
				role="button"
				tabIndex={0}
				aria-expanded={expandedId === recording.id}
				aria-controls={`recording-detail-${recording.id}`}
			>
				<div className="flex flex-grow items-center overflow-hidden">
					<label
						className={`${appStyles.recordingHistoryItemSelectWrapper} mr-3 cursor-pointer`}
						onClick={(e) => e.stopPropagation()}
						onKeyDown={(e) => {
							if (e.key === "Enter" || e.key === " ") {
								e.stopPropagation();
							}
						}}
					>
						<input
							type="checkbox"
							className="sr-only"
							checked={selectedRecordings.has(recording.id)}
							onChange={(e) => {
								e.stopPropagation();
								toggleSelection(recording.id, e as unknown as React.MouseEvent);
							}}
							aria-label={t.recordingItem.selectRecording}
						/>
						<div
							className={`${appStyles.recordingHistoryItemSelectBox(selectedRecordings.has(recording.id))} w-5 h-5 md:w-6 md:h-6`}
							role="presentation"
						>
							{selectedRecordings.has(recording.id) && (
								<span className={appStyles.recordingHistoryItemSelectCheck}>✓</span>
							)}
						</div>
					</label>
					<span className={`${appStyles.recordingHistoryItemIcon} text-lg mr-3`} aria-hidden="true">🎵</span>
					<div className="flex-1 min-w-0">
						<div className={`${appStyles.recordingHistoryItemFileName} flex justify-between items-center`}>
							<span className="truncate pr-2">{recording.mp3File ? recording.mp3File.name : recording.file.name}</span>
							<div className="flex items-center gap-1 ml-1 shrink-0">
								<button
									type="button"
									onClick={(e) => {
										e.stopPropagation();
										downloadFile(recording);
									}}
									onKeyDown={(e) => {
										if (e.key === "Enter" || e.key === " ") {
											e.stopPropagation();
											downloadFile(recording);
										}
									}}
									className={appStyles.actionButtonSecondary}
									title={recording.mp3File ? t.recordingItem.downloadMP3 : t.recordingItem.downloadWebM}
									aria-label={recording.mp3File ? t.recordingItem.downloadMP3 : t.recordingItem.downloadWebM}
								>
									<span aria-hidden="true">{recording.mp3File ? "📥" : "🔊"}</span>
								</button>
								{recording.transcriptionText?.trim() && (
									<button
										type="button"
										onClick={(e) => {
											e.stopPropagation();
											downloadTranscriptionText(recording.transcriptionText || "", recording.timestamp);
										}}
										onKeyDown={(e) => {
											if (e.key === "Enter" || e.key === " ") {
												e.stopPropagation();
												downloadTranscriptionText(recording.transcriptionText || "", recording.timestamp);
											}
										}}
										className={appStyles.actionButtonSecondary}
										title={t.recordingItem.downloadTranscription}
										aria-label={t.recordingItem.downloadTranscription}
									>
										<span aria-hidden="true">📝</span>
									</button>
								)}
								<button
									type="button"
									onClick={(e) => {
										e.stopPropagation();
										if (confirm(t.recordingItem.deleteConfirmation)) {
											deleteRecording(recording.id);
										}
									}}
									onKeyDown={(e) => {
										if (e.key === "Enter" || e.key === " ") {
											e.stopPropagation();
											if (confirm(t.recordingItem.deleteConfirmation)) {
												deleteRecording(recording.id);
											}
										}
									}}
									className={appStyles.actionButtonSecondary}
									title={t.recordingItem.delete}
									aria-label={t.recordingItem.deleteRecording}
								>
									<span aria-hidden="true">🗑️</span>
								</button>
							</div>
						</div>
						<div className={`${appStyles.recordingHistoryItemMeta} flex items-center`}>
							<span className="whitespace-nowrap">{formatDate(recording.timestamp)}</span>
							<span className="mx-2" aria-hidden="true">•</span>
							<span>{formatDuration(recording.duration)}</span>
							<span className="mx-2" aria-hidden="true">•</span>
							<span className={appStyles.recordingHistoryItemFileSize}>
								{formatFileSize(recording.file)}
							</span>

							{/* NEW バッジ表示 - 現在のセッションと一致する場合、または1時間以内に作成された場合 */}
							{(recording.sessionId === getSessionId() ||
								(recording.createdAt && isWithinLastHour(recording.createdAt))) && (
								<span className="ml-2 px-1.5 py-0.5 text-xs font-bold bg-blue-500 text-white rounded">NEW</span>
							)}
							<span className="ml-2" aria-hidden="true">
								<span
									className={`${appStyles.recordingHistoryItemExpandIcon(expandedId === recording.id)} transform transition-transform ${expandedId === recording.id ? "rotate-180" : ""}`}
								>
									▼
								</span>
							</span>
						</div>
					</div>
				</div>
			</div>
			{expandedId === recording.id && (
				<RecordingItemDetail
					recording={recording}
					formatFileSize={formatFileSize}
					formatDuration={formatDuration}
				/>
			)}
		</div>
	);
};

interface RecordingItemDetailProps {
	recording: RecordingFile;
	formatFileSize: (file: File) => string;
	formatDuration: (seconds: number) => string;
}

const RecordingItemDetail: React.FC<RecordingItemDetailProps> = ({
	recording,
	formatFileSize,
	formatDuration,
}) => {
	const t = useTranslation();

	return (
		<div
			className={appStyles.recordingHistoryItemDetail}
			id={`recording-detail-${recording.id}`}
		>
			{/* オーディオプレーヤー - 上下のマージンを追加 */}
			<div className="my-4">
				<audio
					controls
					className={appStyles.recordingHistoryAudio}
					src={URL.createObjectURL(recording.mp3File || recording.file)}
				>
					{/* Caption track is removed since no actual captions are available and empty src causes warnings */}
				</audio>
				{recording.mp3File && (
					<div className="text-xs mt-1 text-right text-green-600 dark:text-green-400">{t.recordingItem.playingMP3}</div>
				)}
			</div>
			{/* メタデータとアクションボタン - 間隔を調整 */}
			<div
				className={`${appStyles.recordingHistoryItemDetailMetaWrapper} my-5 p-4 rounded-lg bg-gray-100/70 dark:bg-gray-800/30`}
			>
				<div className={`${appStyles.recordingHistoryItemDetailMeta} space-y-2`}>
					<p className="flex items-baseline">
						<span className="font-medium mr-2 min-w-[5rem]">{t.recordingItem.fileName}:</span>
						{recording.mp3File ? recording.mp3File.name : recording.file.name}
					</p>
					<p className="flex items-baseline">
						<span className="font-medium mr-2 min-w-[5rem]">{t.recordingItem.fileSize}:</span> {formatFileSize(recording.file)}
					</p>
					<p className="flex items-baseline">
						<span className="font-medium mr-2 min-w-[5rem]">{t.recordingItem.length}:</span> {formatDuration(recording.duration)}
					</p>

					{/* MP3変換状態 */}
					<div className="mt-3 pt-3 border-t border-gray-300/50 dark:border-gray-700/50">
						{recording.isConverting ? (
							<div className="flex items-center">
								<div className="animate-spin mr-2 h-4 w-4 border-2 border-blue-500 rounded-full border-t-transparent" />
								<p className="text-sm text-blue-600 dark:text-blue-400">{t.recordingItem.convertingToMP3}</p>
							</div>
						) : recording.mp3File ? (
							<div className="flex justify-between items-center">
								<div className="flex items-center">
									<span className="text-green-600 dark:text-green-400 mr-2" aria-hidden="true">✓</span>
									<p className="text-sm">
										<span className="text-green-600 dark:text-green-400">{t.recordingItem.mp3ConversionComplete}</span>
										<span className="ml-2 text-gray-500 dark:text-gray-400">({formatFileSize(recording.mp3File)})</span>
									</p>
								</div>
							</div>
						) : (
							<p className="text-sm text-gray-500 dark:text-gray-400">{t.recordingItem.noMP3File}</p>
						)}
					</div>
				</div>
			</div>
			{/* 文字起こしセクション - マージン調整 */}
			<div className="mt-6 border-t border-gray-300/50 dark:border-gray-700/50 pt-5 px-0 pb-4">
				<h4 className="font-medium text-gray-700 dark:text-gray-300 mb-4 text-sm flex items-center px-1">
					<div className="flex items-center">
						<span className="mr-2 text-base" aria-hidden="true">📝</span>
						<span className="text-base">{t.recordingItem.transcription}</span>
					</div>
				</h4>
				{recording.transcriptionText?.trim() ? (
					<pre className="text-sm whitespace-pre-wrap text-gray-700 font-mono bg-gray-100 p-5 rounded-lg max-h-[350px] overflow-y-auto border border-gray-200/80 shadow-inner leading-relaxed my-2 mx-0.5 dark:text-gray-300 dark:bg-gray-900/40 dark:border-gray-700/30">
						{recording.transcriptionText}
					</pre>
				) : (
					<p className="text-sm italic text-gray-500 py-6 px-4 bg-gray-100 rounded-lg flex items-center justify-center space-x-3 my-3 dark:bg-gray-800/30">
						<span className="text-base" aria-hidden="true">📝</span>
						<span>{t.recordingItem.noTranscription}</span>
					</p>
				)}
			</div>
		</div>
	);
};

export default RecordingItem;
